// CIVIX demo-mode mock. Activated when VITE_DEMO_MODE === 'true'.
// Provides in-memory responses for all API endpoints so the deployed
// static site is fully interactive without a backend.
import dayjs from 'dayjs';

const now = () => new Date().toISOString();
const uid = () => Math.random().toString(36).slice(2, 10);

// ------------------------------ Seed data ------------------------------
const TIERS = ['CIVIX FEX Tier 1', 'CIVIX FEX Tier 2', 'CIVIX FEX Tier 3', 'CIVIX Gen Life Tier 1', 'CIVIX Gen Life Tier 2', 'CIVIX MP Tier 1', 'CIVIX MP Tier 2'];
const VERTICALS = ['Final Expense', 'Term Life', 'Whole Life', 'IUL', 'Mortgage Protection'];
const DISPOS = ['New', 'Hot', 'Warm', 'Cold', 'Sold', 'Not Interested', 'Bad Number', 'Do Not Call'];
const STAGES = ['New Lead', 'Contacted', 'Quoted', 'App Submitted', 'Underwriting', 'Issued/Sold', 'Lost'];
const FIRSTS = ['John', 'Mary', 'Robert', 'Patricia', 'James', 'Jennifer', 'Michael', 'Linda', 'William', 'Elizabeth', 'David', 'Barbara', 'Richard', 'Susan', 'Joseph', 'Margaret', 'Thomas', 'Nancy', 'Charles', 'Karen'];
const LASTS = ['Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis', 'Rodriguez', 'Martinez', 'Hernandez', 'Lopez', 'Gonzalez', 'Wilson', 'Anderson', 'Thomas', 'Taylor', 'Moore', 'Jackson', 'Martin'];
const STATES = ['MT', 'TX', 'FL', 'CA', 'AZ', 'CO', 'NV', 'UT', 'ID', 'OR', 'WA', 'NM', 'OK', 'KS', 'NE'];
const VENDORS = ['SmartFinancial', 'Benepath', 'Agency Incline', 'QuoteWizard', 'NextGen Leads', 'USHA', 'Everquote'];

// 12-carrier e-app registry (mirrors api/src/services/carrierRegistry.js)
const CARRIERS = [
  { id: 'americo', name: 'Americo', products: ['Final Expense', 'Term Life'], agentPortalUrl: 'https://agents.americo.com/eapp', auth: 'agent_login', color: '#003366', notes: 'Eagle Premier / Ultra Protector for FEX. Voice-sig available — fastest path to issued.' },
  { id: 'mutual_of_omaha', name: 'Mutual of Omaha', products: ['Final Expense', 'Whole Life', 'Med Supp'], agentPortalUrl: 'https://www.mutualofomaha.com/broker/eapp', auth: 'agent_login', color: '#0033A0', notes: 'Living Promise (FEX) and Term Life Express. Same-day decision common.' },
  { id: 'aig_corebridge', name: 'AIG / Corebridge', products: ['Term Life', 'IUL', 'Whole Life'], agentPortalUrl: 'https://eapp.corebridgefinancial.com', auth: 'agent_login', color: '#0050A0', notes: 'Select-a-Term, Max Accumulator+, QoL Guaranteed Issue.' },
  { id: 'transamerica', name: 'TransAmerica', products: ['Final Expense', 'Term Life', 'Whole Life', 'IUL'], agentPortalUrl: 'https://www.transamerica.com/individual/agents/eapp', auth: 'agent_login', color: '#A6192E', notes: 'Trendsetter LB, Financial Foundation IUL, Immediate Solution FEX.' },
  { id: 'american_amicable', name: 'American Amicable', products: ['Final Expense', 'Term Life'], agentPortalUrl: 'https://americanamicable.com/eapp', auth: 'agent_login', color: '#0F5C8B', notes: 'Senior Choice + Survivor Whole Life. Voice-signature workflow available.' },
  { id: 'aflac_fex', name: 'Aflac (FEX Only)', products: ['Final Expense'], agentPortalUrl: 'https://www.aflac.com/agents/eapp', auth: 'agent_login', color: '#0089CF', notes: 'Final Expense whole life only. Brand recognition closes deals.' },
  { id: 'aetna_fex', name: 'Aetna (FEX Only)', products: ['Final Expense'], agentPortalUrl: 'https://producer.aetnaseniorproducts.com/eapp', auth: 'agent_login', color: '#7D3F98', notes: 'Accendo Insurance (Aetna brand) — Whole Life Final Expense.' },
  { id: 'royal_neighbors', name: 'Royal Neighbors of America', products: ['Final Expense', 'Term Life', 'Whole Life'], agentPortalUrl: 'https://www.royalneighbors.org/agent/eapp', auth: 'agent_login', color: '#702082', notes: 'Simplified-issue Whole Life. Strong female-focused senior branding.' },
  { id: 'ethos', name: 'Ethos', products: ['Term Life', 'Whole Life'], agentPortalUrl: 'https://www.ethoslife.com/agents/eapp', auth: 'sso', color: '#1A1A1A', notes: 'Fully digital, instant decision. Best UX of the bunch.' },
  { id: 'chubb', name: 'Chubb', products: ['Term Life', 'Whole Life', 'Final Expense'], agentPortalUrl: 'https://eapp.combinedinsurance.com', auth: 'agent_login', color: '#1F2D5C', notes: 'Combined Insurance (Chubb) brand. Solid issue-rate.' },
  { id: 'instabrain', name: 'InstaBrain', products: ['Term Life'], agentPortalUrl: 'https://app.instabrain.com/agents/eapp', auth: 'sso', color: '#FF5A5F', notes: 'Algorithmic underwriting. 60s decision in many cases.' },
  { id: 'national_life_group', name: 'National Life Group', products: ['IUL', 'Whole Life', 'Term Life'], agentPortalUrl: 'https://www.nationallife.com/agent/eapp', auth: 'agent_login', color: '#003F87', notes: 'FlexLife / SummitLife IUL. Strong cash-value engine.' },
];

const rand = (a) => a[Math.floor(Math.random() * a.length)];
const randInt = (a, b) => Math.floor(a + Math.random() * (b - a));

function makeLead(i) {
  const first = rand(FIRSTS);
  const last = rand(LASTS);
  const stage = rand(STAGES);
  const dispo = rand(DISPOS);
  return {
    _id: uid(),
    firstName: first,
    lastName: last,
    name: `${first} ${last}`,
    email: `${first.toLowerCase()}.${last.toLowerCase()}${i}@example.com`,
    phone: `+1${randInt(200, 999)}${randInt(200, 999)}${randInt(1000, 9999)}`,
    state: rand(STATES),
    city: 'Bozeman',
    zip: String(randInt(10000, 99999)),
    timezone: 'America/Denver',
    vertical: rand(VERTICALS),
    tier: rand(TIERS),
    leadVendorName: rand(VENDORS),
    age: randInt(35, 75),
    ageRange: '50-65',
    birthday: dayjs().subtract(randInt(35, 75), 'year').format('YYYY-MM-DD'),
    beneficiary: 'Spouse',
    protectionGoal: 'Final Expense Coverage',
    existingLifeInsurance: Math.random() > 0.5,
    mortgageAmount: randInt(50, 500) * 1000,
    quote: randInt(20, 250),
    cost: randInt(15, 200),
    commission: randInt(200, 2500),
    annualPremium: randInt(500, 5000),
    callCount: randInt(0, 12),
    smsCount: randInt(0, 8),
    blocked: false,
    disposition: dispo,
    stage,
    tags: ['demo'],
    notes: 'Imported from ' + rand(VENDORS),
    score: randInt(40, 99),
    trustScore: randInt(50, 95),
    priority: Math.random() > 0.6 ? 'high' : 'normal',
    assignedAgent: 'easton@civix.local',
    createdAt: dayjs().subtract(randInt(0, 60), 'day').toISOString(),
    updatedAt: now(),
    lastContactAt: dayjs().subtract(randInt(0, 30), 'day').toISOString(),
  };
}

const STATE = {
  agent: {
    _id: 'demo-agent',
    name: 'Easton Padden',
    email: 'easton@civix.local',
    role: 'superadmin',
    timezone: 'America/Denver',
    forwardNumber: '+14068604882',
    avatar: null,
    tenant: 'civix-demo',
  },
  tenant: {
    _id: 'civix-demo',
    name: 'CIVIX Demo',
    domain: 'demo.civix.app',
    primaryColor: '#8b5cf6',
    accentColor: '#22d3ee',
    aiVoice: 'alloy',
    discordWebhook: '',
    plan: 'Pro',
  },
  leads: Array.from({ length: 240 }, (_, i) => makeLead(i)),
  campaigns: [
    { _id: uid(), name: 'Q2 FEX Drip', productType: 'Final Expense', funnelType: 'live', status: 'active', sent: 4231, opened: 1892, replied: 184, type: 'sms', performance: { totalLeads: 4231, totalSales: 162, totalRevenue: 298400, conversionRate: 0.038 }, pricing: { cpl: 18, retailPrice: 25 }, autoScale: { scaledMultiplier: 1.4 }, vendorIds: [], createdAt: now() },
    { _id: uid(), name: 'Welcome Sequence', productType: 'Term Life', funnelType: 'live', status: 'active', sent: 1102, opened: 612, replied: 89, type: 'email', performance: { totalLeads: 1102, totalSales: 71, totalRevenue: 124800, conversionRate: 0.064 }, pricing: { cpl: 22, retailPrice: 30 }, autoScale: { scaledMultiplier: 1.1 }, vendorIds: [], createdAt: now() },
    { _id: uid(), name: 'Mortgage Protection Push', productType: 'Mortgage Protection', funnelType: 'aged', status: 'paused', sent: 567, opened: 201, replied: 23, type: 'sms', performance: { totalLeads: 567, totalSales: 18, totalRevenue: 41200, conversionRate: 0.032 }, pricing: { cpl: 14, retailPrice: 22 }, autoScale: { scaledMultiplier: 0.8 }, vendorIds: [], createdAt: now() },
  ],
  vendors: VENDORS.map((v, idx) => ({
    _id: uid(),
    name: v,
    contactEmail: `partner@${v.toLowerCase().replace(/\s+/g, '')}.com`,
    active: true,
    visibility: idx < 3 ? 'global' : 'private',
    pricePerLead: randInt(8, 35),
    pricingTier: ['economy','standard','premium','exclusive'][idx % 4],
    leadsCount: randInt(50, 800),
    avgCost: randInt(8, 35),
    conversionRate: (Math.random() * 12 + 2).toFixed(1),
    deliveryMethods: { csv: true, webhook: true, email: false, api: idx < 3 },
    deliveryConfig: {
      webhookUrl: '',
      webhookSecret: 'whsec_' + Math.random().toString(36).slice(2, 14),
      emailParseAddress: `${v.toLowerCase().replace(/\s+/g, '')}-${idx}@parse.civix.app`,
      emailParseTemplate: 'Subject contains lead | extract first/last/phone/state/email',
      apiKey: 'civx_pk_' + Math.random().toString(36).slice(2, 12),
      apiSecret: 'civx_sk_' + Math.random().toString(36).slice(2, 18),
    },
    stats: { totalDelivered: randInt(120, 4200), totalSold: randInt(10, 280), avgCloseRate: Math.random() * 0.18 + 0.04, lastDeliveredAt: dayjs().subtract(randInt(0, 14), 'hour').toISOString() },
    deliveries: Array.from({ length: 5 }, (_, k) => ({ at: dayjs().subtract(k, 'hour').toISOString(), count: randInt(1, 12), method: ['csv','webhook','api','email'][k % 4] })),
  })),
  phoneNumbers: [
    { _id: uid(), number: '+14068604882', friendlyName: 'Main Line', state: 'MT', purchased: '2025-09-12' },
    { _id: uid(), number: '+14065551203', friendlyName: 'FEX Campaign', state: 'MT', purchased: '2025-10-01' },
  ],
  inbox: Array.from({ length: 18 }, (_, i) => ({
    _id: uid(),
    from: rand(FIRSTS) + ' ' + rand(LASTS),
    phone: `+1${randInt(200, 999)}${randInt(200, 999)}${randInt(1000, 9999)}`,
    body: rand(['Stop', 'Yes interested', 'Call me later today', 'What are your rates?', 'Not now thanks', 'Send more info']),
    direction: 'inbound',
    read: i > 5,
    createdAt: dayjs().subtract(i, 'hour').toISOString(),
  })),
  notifications: [
    { _id: uid(), title: 'New lead assigned', body: 'Robert Smith — CIVIX FEX Tier 1', read: false, createdAt: now() },
    { _id: uid(), title: 'Discord webhook test passed', body: 'Connected', read: false, createdAt: now() },
    { _id: uid(), title: 'Daily report ready', body: '12 sold, $14,832 premium', read: true, createdAt: now() },
  ],
  aibots: [
    {
      _id: uid(),
      name: 'FEX Concierge',
      status: 'active',
      persona: { name: 'Hailey', role: 'Licensed FEX Concierge', tone: 'warm', voiceId: 'alloy', language: 'en' },
      goals: ['Qualify lead for Final Expense coverage', 'Book appointment with Easton', 'Capture beneficiary + DOB'],
      systemPrompt: 'You are Hailey, a warm and helpful concierge for CIVIX. Speak conversationally. Never give a quote without DOB and state.',
      openingMessage: 'Hi #first_name! This is Hailey from CIVIX. I saw you requested info on final expense coverage — got a quick sec to chat?',
      closingMessage: 'Thanks #first_name — Easton will reach out soon!',
      knowledge: [{ title: 'FEX overview', content: 'Final Expense covers funeral and burial costs, $5k-$25k, ages 50-85.' }],
      guardrails: { maxMessagesPerLead: 12, stopKeywords: ['stop', 'unsubscribe'], handoffKeywords: ['agent', 'human', 'manager'], forbiddenTopics: ['investment advice'], requireDisclosure: true, quietHoursStart: 21, quietHoursEnd: 8 },
      outbound: { cadence: 'standard', fromNumberPool: ['+14068604882'], aiModel: 'gpt-4o-mini', voiceModel: 'tts-1' },
      triggers: { onNewLead: true, onMissedCall: true, onInboundReply: true, onWarmDisposition: false },
      stats: { messagesSent: 3214, callsPlaced: 412, appointmentsBooked: 87, leadsTouched: 891 },
      createdAt: now(),
    },
    {
      _id: uid(),
      name: 'Re-engage Cold',
      status: 'active',
      persona: { name: 'Marcus', role: 'Senior Advisor', tone: 'direct', voiceId: 'onyx', language: 'en' },
      goals: ['Reactivate cold leads', 'Schedule callback'],
      openingMessage: 'Hey #first_name, Marcus with CIVIX. Got 2 minutes? I think I can save you money on coverage.',
      stats: { messagesSent: 1840, callsPlaced: 142, appointmentsBooked: 31, leadsTouched: 612 },
      guardrails: { maxMessagesPerLead: 6, stopKeywords: ['stop'], handoffKeywords: ['agent', 'human'], forbiddenTopics: [], requireDisclosure: true, quietHoursStart: 21, quietHoursEnd: 8 },
      outbound: { cadence: 'aggressive', fromNumberPool: ['+14065551203'], aiModel: 'gpt-4o-mini', voiceModel: 'tts-1' },
      triggers: { onNewLead: false, onMissedCall: true, onInboundReply: true, onWarmDisposition: false },
      knowledge: [],
      createdAt: now(),
    },
    {
      _id: uid(),
      name: 'Outbound Voice Caller',
      status: 'paused',
      persona: { name: 'Aria', role: 'Outbound Voice Agent', tone: 'professional', voiceId: 'shimmer', language: 'en' },
      goals: ['Verify lead intent', 'Transfer warm leads to live agent'],
      openingMessage: 'Hi #first_name, this is Aria calling from CIVIX about your insurance quote request. Is now a good time?',
      stats: { messagesSent: 0, callsPlaced: 1240, appointmentsBooked: 142, leadsTouched: 982 },
      guardrails: { maxMessagesPerLead: 1, stopKeywords: ['stop'], handoffKeywords: ['agent', 'human', 'representative'], forbiddenTopics: [], requireDisclosure: true, quietHoursStart: 21, quietHoursEnd: 8 },
      outbound: { cadence: 'standard', fromNumberPool: ['+14068604882'], aiModel: 'gpt-4o-mini', voiceModel: 'tts-1' },
      triggers: { onNewLead: true, onMissedCall: false, onInboundReply: false, onWarmDisposition: true },
      knowledge: [],
      createdAt: now(),
    },
  ],
  a2p: {
    _id: 'demo-a2p',
    brand: {
      legalBusinessName: 'CIVIX Insurance Group LLC',
      ein: '88-1234567',
      entityType: 'PRIVATE_PROFIT',
      industry: 'INSURANCE',
      website: 'https://civix.app',
      email: 'compliance@civix.app',
      phone: '+14068604882',
      street: '123 Main St',
      city: 'Bozeman',
      state: 'MT',
      zip: '59715',
      country: 'US',
      contactFirstName: 'Easton',
      contactLastName: 'Padden',
      contactTitle: 'Owner',
    },
    brandStatus: 'APPROVED',
    brandId: 'BN-DEMO-2026',
    campaign: {
      useCase: 'MIXED',
      description: 'CIVIX sends life insurance quote follow-ups, appointment confirmations, and customer service messages to consumers who have requested information about life, final expense, or mortgage protection coverage.',
      sampleMessage1: 'Hi #first_name, this is #agent_name with CIVIX. Your life insurance quote is ready — coverage starts at $#quote/mo. Reply YES to schedule.',
      sampleMessage2: 'Hi #first_name — confirming your appointment tomorrow at 2pm. Reply C to confirm or R to reschedule. Reply STOP to opt out.',
      optInKeywords: 'START, YES, UNSTOP',
      optOutKeywords: 'STOP, UNSUBSCRIBE, END, QUIT, CANCEL',
      helpKeywords: 'HELP, INFO',
      optInMessage: 'CIVIX: You are now opted in. Msg & data rates may apply. Reply HELP for help, STOP to cancel.',
      optOutMessage: 'CIVIX: You are unsubscribed and will receive no further messages.',
      helpMessage: 'CIVIX: For help, call (406) 860-4882. Reply STOP to opt out. Msg & data rates may apply.',
      embeddedLink: false,
      embeddedPhone: true,
      ageGated: false,
    },
    campaignStatus: 'APPROVED',
    campaignId: 'CAMP-DEMO-2026',
    attachedNumbers: ['+14068604882'],
    events: [
      { type: 'BRAND_CREATED', message: 'Brand draft saved', at: dayjs().subtract(14, 'day').toISOString() },
      { type: 'BRAND_SUBMITTED', message: 'Submitted to TCR', at: dayjs().subtract(13, 'day').toISOString() },
      { type: 'BRAND_APPROVED', message: 'Brand verified by TCR', at: dayjs().subtract(11, 'day').toISOString() },
      { type: 'CAMPAIGN_SUBMITTED', message: 'Campaign submitted for carrier review', at: dayjs().subtract(10, 'day').toISOString() },
      { type: 'CAMPAIGN_APPROVED', message: 'Carrier approval received', at: dayjs().subtract(4, 'day').toISOString() },
      { type: 'NUMBER_ATTACHED', message: '+14068604882 attached to campaign', at: dayjs().subtract(4, 'day').toISOString() },
    ],
  },  drips: [
    { _id: 'drip-1', name: 'FEX Welcome 7-Day', status: 'active', steps: [
      { type: 'sms', body: 'Hi #first_name, welcome to CIVIX! Your FEX info packet is on its way.', delayMinutes: 0 },
      { type: 'wait', delayMinutes: 60 },
      { type: 'ai_message', prompt: 'Friendly check-in to see if they have questions', delayMinutes: 0 },
      { type: 'wait', delayMinutes: 1440 },
      { type: 'sms', body: 'Hey #first_name — your quote is $#quote/mo for $10k coverage. Want to lock it in?', delayMinutes: 0 },
    ], trigger: 'new_lead', audience: { tier: 'CIVIX FEX Tier 1' }, schedule: { sendDays: ['mon','tue','wed','thu','fri'], sendWindowStart: 9, sendWindowEnd: 19, timezone: 'America/Denver', respectQuietHours: true }, stats: { sent: 1240, replied: 184, booked: 47, opted: 12 }, createdAt: now() },
    { _id: 'drip-2', name: 'No Answer Re-engage', status: 'active', steps: [], trigger: 'no_contact', audience: {}, schedule: { sendDays: ['mon','tue','wed','thu','fri'], sendWindowStart: 10, sendWindowEnd: 20, timezone: 'America/Denver', respectQuietHours: true }, stats: { sent: 892, replied: 102, booked: 19 }, createdAt: now() },
    { _id: 'drip-3', name: 'Quoted Not Sold', status: 'paused', steps: [], trigger: 'disposition', audience: { disposition: 'Warm' }, schedule: { sendDays: ['mon','tue','wed','thu','fri'], sendWindowStart: 9, sendWindowEnd: 18, timezone: 'America/Denver', respectQuietHours: true }, stats: { sent: 412, replied: 51, booked: 8 }, createdAt: now() },
  ],
};

// ------------------------------ Routing ------------------------------
function paginate(arr, params = {}) {
  const page = +(params.page || 1);
  const limit = +(params.limit || 25);
  const start = (page - 1) * limit;
  return { items: arr.slice(start, start + limit), total: arr.length, page, limit };
}

function filterLeads(leads, params = {}) {
  let r = [...leads];
  if (params.search) {
    const q = String(params.search).toLowerCase();
    r = r.filter((l) => l.name.toLowerCase().includes(q) || l.email.toLowerCase().includes(q) || l.phone.includes(q));
  }
  if (params.tier) r = r.filter((l) => l.tier === params.tier);
  if (params.state) r = r.filter((l) => l.state === params.state);
  if (params.disposition) r = r.filter((l) => l.disposition === params.disposition);
  if (params.stage) r = r.filter((l) => l.stage === params.stage);
  if (params.vertical) r = r.filter((l) => l.vertical === params.vertical);
  return r;
}

function dispatch(method, url, body, params) {
  const m = (re, fn) => {
    const match = url.match(re);
    if (match) return { handled: true, value: fn(match, body, params) };
    return null;
  };

  // Auth
  if (url === '/auth/login' && method === 'POST') {
    return { token: 'demo-token-' + uid(), agent: STATE.agent };
  }
  if (url === '/auth/register' && method === 'POST') {
    return { token: 'demo-token-' + uid(), agent: { ...STATE.agent, email: body?.email || STATE.agent.email, name: body?.name || STATE.agent.name } };
  }
  if (url === '/auth/me' && method === 'GET') {
    return { agent: STATE.agent };
  }

  // Tenant
  if (url === '/tenant' && method === 'GET') return STATE.tenant;
  if (url === '/tenant/full' && method === 'GET') return STATE.tenant;
  if (url === '/tenant' && method === 'PUT') { Object.assign(STATE.tenant, body || {}); return STATE.tenant; }
  if (url === '/tenant/discord/test' && method === 'POST') return { ok: true, sentAt: now() };

  // Dashboard / stats
  if (url === '/dashboard/stats' && method === 'GET') {
    return {
      totalLeads: STATE.leads.length,
      newToday: 12,
      hot: STATE.leads.filter((l) => l.disposition === 'Hot').length,
      sold: STATE.leads.filter((l) => l.disposition === 'Sold').length,
      callsToday: 47,
      smsToday: 132,
      revenueToday: 3284,
      revenueMonth: 47120,
      pipeline: STATE.leads.length * 540,
      conversionRate: 14.2,
      avgDealSize: 1840,
      callsAnswered: 38,
      callsMissed: 9,
      activeAgents: 4,
    };
  }
  if (url === '/dashboard/periods' && method === 'GET') {
    const dailyDials = randInt(72, 96);
    const dailyConnects = Math.round(dailyDials * (0.28 + Math.random() * 0.08));
    const dailyTalk5 = Math.round(dailyConnects * (0.42 + Math.random() * 0.12));
    const dailyBooked = Math.round(dailyConnects * (0.32 + Math.random() * 0.08));
    const dailyPres = Math.round(dailyBooked * (0.62 + Math.random() * 0.10));
    const dailyCloses = Math.max(1, Math.round(dailyPres * (0.30 + Math.random() * 0.08)));
    const dailyPremium = dailyCloses * randInt(1100, 1850);
    const mk = (mult, jitter = 0.05) => {
      const j = (1 + (Math.random() * 2 - 1) * jitter);
      const dials = Math.round(dailyDials * mult * j);
      const connects = Math.round(dailyConnects * mult * j);
      const talkOver5Min = Math.round(dailyTalk5 * mult * j);
      const bookedAppts = Math.round(dailyBooked * mult * j);
      const presentations = Math.round(dailyPres * mult * j);
      const closes = Math.max(1, Math.round(dailyCloses * mult * j));
      const premium = closes * randInt(1100, 1850);
      return { dials, connects, talkOver5Min, bookedAppts, presentations, closes, premium };
    };
    return {
      daily: { dials: dailyDials, connects: dailyConnects, talkOver5Min: dailyTalk5, bookedAppts: dailyBooked, presentations: dailyPres, closes: dailyCloses, premium: dailyPremium },
      wtd: mk(5),
      mtd: mk(22),
      trend14: Array.from({ length: 14 }, (_, i) => ({ d: dayjs().subtract(13 - i, 'day').format('MM/DD'), v: Math.round(dailyDials * (0.7 + Math.random() * 0.6)) })),
    };
  }
  if (url === '/dashboard/charts' && method === 'GET') {
    return {
      leadsByDay: Array.from({ length: 30 }, (_, i) => ({ date: dayjs().subtract(29 - i, 'day').format('MM/DD'), count: randInt(5, 30) })),
      callsByHour: Array.from({ length: 12 }, (_, i) => ({ hour: `${8 + i}:00`, calls: randInt(3, 25) })),
      revenueByWeek: Array.from({ length: 12 }, (_, i) => ({ week: `W${i + 1}`, revenue: randInt(3000, 18000) })),
      pipelineByStage: STAGES.map((s) => ({ stage: s, count: STATE.leads.filter((l) => l.stage === s).length })),
      dispositions: DISPOS.map((d) => ({ name: d, value: STATE.leads.filter((l) => l.disposition === d).length })),
    };
  }

  // Leads
  if (url === '/leads' && method === 'GET') {
    const filtered = filterLeads(STATE.leads, params);
    return paginate(filtered, params);
  }
  if (url === '/leads' && method === 'POST') {
    const lead = { ...makeLead(STATE.leads.length), ...body, _id: uid(), createdAt: now() };
    STATE.leads.unshift(lead);
    return lead;
  }
  let r;
  if ((r = m(/^\/leads\/([^/]+)$/, (mt) => STATE.leads.find((l) => l._id === mt[1])))) return r.value;
  if (method === 'PUT' && (r = m(/^\/leads\/([^/]+)$/, (mt, b) => { const l = STATE.leads.find((l) => l._id === mt[1]); if (l) Object.assign(l, b, { updatedAt: now() }); return l; }))) return r.value;
  if (method === 'DELETE' && (r = m(/^\/leads\/([^/]+)$/, (mt) => { STATE.leads = STATE.leads.filter((l) => l._id !== mt[1]); return { ok: true }; }))) return r.value;
  if (url === '/leads/import' && method === 'POST') return { imported: 42, skipped: 3 };
  if (url === '/leads/export' && method === 'GET') return { url: '#', count: STATE.leads.length };

  // Campaigns
  if (url === '/campaigns' && method === 'GET') return { campaigns: STATE.campaigns };
  if (url === '/campaigns' && method === 'POST') {
    const c = { _id: uid(), sent: 0, opened: 0, replied: 0, status: 'draft', vendorIds: [], performance: { totalLeads: 0, totalSales: 0, totalRevenue: 0, conversionRate: 0 }, autoScale: { scaledMultiplier: 1.0 }, createdAt: now(), ...body };
    STATE.campaigns.unshift(c);
    return c;
  }
  if ((r = m(/^\/campaigns\/([^/]+)\/toggle$/, (mt) => {
    const c = STATE.campaigns.find((x) => x._id === mt[1]);
    if (c) c.status = c.status === 'active' ? 'paused' : 'active';
    return c;
  }))) return r.value;
  if ((r = m(/^\/campaigns\/([^/]+)\/connect-vendor$/, (mt, b) => {
    const c = STATE.campaigns.find((x) => x._id === mt[1]);
    const v = STATE.vendors.find((x) => x._id === b?.vendorId);
    if (!c || !v) return { ok: false };
    c.vendorIds = Array.from(new Set([...(c.vendorIds || []), v._id]));
    return { ok: true, campaign: c, vendor: v };
  }))) return r.value;
  if ((r = m(/^\/campaigns\/([^/]+)\/disconnect-vendor$/, (mt, b) => {
    const c = STATE.campaigns.find((x) => x._id === mt[1]);
    if (c) c.vendorIds = (c.vendorIds || []).filter((id) => id !== b?.vendorId);
    return { ok: true };
  }))) return r.value;

  // Vendors
  if (url === '/vendors' && method === 'GET') return { vendors: STATE.vendors };
  if (url === '/vendors' && method === 'POST') {
    const v = {
      _id: uid(), active: true, leadsCount: 0, visibility: 'private', pricePerLead: 25, pricingTier: 'standard',
      deliveryMethods: { csv: true, webhook: false, email: false, api: false },
      deliveryConfig: { webhookUrl: '', webhookSecret: 'whsec_' + uid(), emailParseAddress: '', emailParseTemplate: '', apiKey: 'civx_pk_' + uid(), apiSecret: 'civx_sk_' + uid() + uid() },
      stats: { totalDelivered: 0, totalSold: 0, avgCloseRate: 0, lastDeliveredAt: null },
      deliveries: [],
      ...body,
    };
    STATE.vendors.unshift(v);
    return v;
  }
  if ((r = m(/^\/vendors\/([^/]+)$/, (mt, b) => {
    const v = STATE.vendors.find((x) => x._id === mt[1]);
    if (method === 'GET') return v || null;
    if (method === 'PUT' && v) { Object.assign(v, b, { updatedAt: now() }); return v; }
    if (method === 'DELETE') { STATE.vendors = STATE.vendors.filter((x) => x._id !== mt[1]); return { ok: true }; }
    return null;
  }))) return r.value;
  if ((r = m(/^\/vendors\/([^/]+)\/test-delivery$/, (mt, b) => {
    const v = STATE.vendors.find((x) => x._id === mt[1]);
    if (!v) return { ok: false };
    v.stats.totalDelivered = (v.stats.totalDelivered || 0) + 1;
    v.stats.lastDeliveredAt = now();
    v.deliveries = [{ at: now(), count: 1, method: b?.method || 'webhook', sample: true }, ...(v.deliveries || []).slice(0, 9)];
    return { ok: true, deliveredAt: v.stats.lastDeliveredAt, sampleLeadId: uid() };
  }))) return r.value;
  if ((r = m(/^\/vendors\/ingest\/([^/]+)$/, (mt, b) => {
    const v = STATE.vendors.find((x) => x._id === mt[1]);
    if (!v) return { ok: false, error: 'Vendor not found' };
    const incoming = Array.isArray(b) ? b : (b?.leads || [b || {}]);
    let added = 0, deduped = 0;
    for (const row of incoming) {
      const phone = String(row.phone || '').replace(/\D/g, '');
      if (!phone) continue;
      if (STATE.leads.some((l) => l.phone.replace(/\D/g, '').slice(-10) === phone.slice(-10))) { deduped++; continue; }
      const lead = {
        ...makeLead(STATE.leads.length),
        firstName: row.firstName || row.first_name || 'New',
        lastName: row.lastName || row.last_name || 'Lead',
        name: ((row.firstName || row.first_name || 'New') + ' ' + (row.lastName || row.last_name || 'Lead')),
        phone: phone.startsWith('1') ? '+' + phone : '+1' + phone.slice(-10),
        email: row.email || `lead${STATE.leads.length}@${v.name.toLowerCase().replace(/\s+/g, '')}.com`,
        state: row.state || 'MT',
        zip: row.zip || '59715',
        leadVendorName: v.name,
        vendorId: v._id,
        campaignId: row.campaignId || (STATE.campaigns[0]?._id),
        notes: 'Ingested via ' + (b?.method || 'API') + ' from ' + v.name,
        createdAt: now(),
      };
      STATE.leads.unshift(lead);
      added++;
    }
    v.stats.totalDelivered = (v.stats.totalDelivered || 0) + added;
    v.stats.lastDeliveredAt = now();
    v.deliveries = [{ at: now(), count: added, method: b?.method || 'api' }, ...(v.deliveries || []).slice(0, 9)];
    return { ok: true, added, deduped, vendorId: v._id };
  }))) return r.value;

  // Phone numbers
  if (url === '/admin/phone-pool' && method === 'GET') return { numbers: STATE.phoneNumbers };
  if (url === '/admin/phone-pool/purchase' && method === 'POST') { const p = { _id: uid(), number: `+1406555${randInt(1000, 9999)}`, friendlyName: 'New Number', state: 'MT', purchased: now() }; STATE.phoneNumbers.unshift(p); return p; }

  // Inbox
  if (url === '/inbox' && method === 'GET') return STATE.inbox;
  if (url === '/inbox/scheduled' && method === 'GET') return [];
  if (url === '/inbox/sent' && method === 'GET') return [];
  if (url === '/inbox/rejected' && method === 'GET') return [];
  if ((r = m(/^\/inbox\/([^/]+)\/read$/, (mt) => { const i = STATE.inbox.find((x) => x._id === mt[1]); if (i) i.read = true; return { ok: true }; }))) return r.value;
  if ((r = m(/^\/inbox\/([^/]+)$/, (mt) => { STATE.inbox = STATE.inbox.filter((x) => x._id !== mt[1]); return { ok: true }; }))) return r.value;

  // Calendar
  if (url === '/calendar/events' && method === 'GET') {
    return Array.from({ length: 8 }, (_, i) => ({
      _id: uid(),
      title: rand(['Call w/ Robert Smith', 'Quote review — Mary Johnson', 'App walkthrough — James Brown', 'Underwriting follow-up']),
      start: dayjs().add(i, 'day').hour(9 + (i % 6)).toISOString(),
      end: dayjs().add(i, 'day').hour(10 + (i % 6)).toISOString(),
      type: 'call',
    }));
  }

  // Reports
  if (url.startsWith('/reports/')) {
    return {
      rows: Array.from({ length: 12 }, (_, i) => ({
        label: dayjs().subtract(11 - i, 'day').format('MM/DD'),
        calls: randInt(20, 80),
        connected: randInt(8, 50),
        sms: randInt(40, 150),
        sold: randInt(0, 6),
        premium: randInt(800, 6000),
      })),
      total: { calls: 612, connected: 287, sms: 1432, sold: 38, premium: 47120 },
    };
  }

  // Drips
  if (url === '/drips' && method === 'GET') return STATE.drips;
  if (url === '/drips' && method === 'POST') {
    const d = { _id: uid(), status: 'draft', steps: [], stats: { sent: 0, replied: 0, booked: 0 }, createdAt: now(), ...body };
    STATE.drips.unshift(d);
    return d;
  }
  if ((r = m(/^\/drips\/([^/]+)$/, (mt, b) => {
    if (mt[1] === 'new') return null;
    const d = STATE.drips.find((x) => x._id === mt[1]);
    if (method === 'GET') return d || null;
    if (method === 'PUT' && d) { Object.assign(d, b, { updatedAt: now() }); return d; }
    if (method === 'DELETE') { STATE.drips = STATE.drips.filter((x) => x._id !== mt[1]); return { ok: true }; }
    return null;
  }))) return r.value;

  // Lead imports
  if (url === '/imports/leads/preview' && method === 'POST') {
    return { ok: true, headers: ['First', 'Last', 'Phone', 'Email', 'State'], rowCount: 248, sample: [] };
  }
  if (url === '/imports/leads/commit' && method === 'POST') {
    const total = body?.rows?.length || body?.total || 248;
    const inserted = Math.floor(total * 0.92);
    const duplicates = Math.floor(total * 0.05);
    const invalid = total - inserted - duplicates;
    // Add a few of the rows to STATE.leads to make it feel real
    if (Array.isArray(body?.rows)) {
      body.rows.slice(0, Math.min(20, inserted)).forEach((row, i) => {
        const lead = makeLead(STATE.leads.length + i);
        Object.assign(lead, {
          firstName: row.firstName || lead.firstName,
          lastName: row.lastName || lead.lastName,
          name: `${row.firstName || lead.firstName} ${row.lastName || lead.lastName}`,
          phone: row.phone || lead.phone,
          email: row.email || lead.email,
          state: row.state || lead.state,
          tier: body?.defaults?.tier || lead.tier,
          vertical: body?.defaults?.vertical || lead.vertical,
          leadVendorName: body?.defaults?.vendor || lead.leadVendorName,
          tags: ['imported', ...(body?.defaults?.tags || [])],
        });
        STATE.leads.unshift(lead);
      });
    }
    return { ok: true, total, inserted, duplicates, invalid, batchId: uid() };
  }
  if (url === '/imports/leads/history' && method === 'GET') {
    return [
      { _id: uid(), filename: 'smartfinancial-april.csv', total: 412, inserted: 389, duplicates: 18, invalid: 5, createdAt: dayjs().subtract(1, 'day').toISOString() },
      { _id: uid(), filename: 'benepath-mar26.csv', total: 248, inserted: 228, duplicates: 14, invalid: 6, createdAt: dayjs().subtract(3, 'day').toISOString() },
    ];
  }

  // AI Bots
  if (url === '/aibots' && method === 'GET') return STATE.aibots;
  if (url === '/aibots' && method === 'POST') {
    const b = { _id: uid(), status: 'active', stats: { messagesSent: 0, callsPlaced: 0, appointmentsBooked: 0, leadsTouched: 0 }, createdAt: now(), ...body };
    STATE.aibots.unshift(b);
    return b;
  }
  if ((r = m(/^\/aibots\/([^/]+)\/test$/, (mt, b) => {
    const bot = STATE.aibots.find((x) => x._id === mt[1]);
    const userMsg = (b?.message || '').toLowerCase();
    let reply = bot?.openingMessage || 'Hi! Thanks for reaching out.';
    if (/stop|unsubscribe/i.test(userMsg)) reply = 'You have been unsubscribed. Reply START to opt back in.';
    else if (/agent|human|representative|manager/i.test(userMsg)) reply = 'Of course — transferring you to a licensed agent now. Stand by.';
    else if (/quote|price|cost/i.test(userMsg)) reply = 'I can pull a quote in 30 seconds. Can you confirm your DOB and ZIP?';
    else if (/yes|interested|sure/i.test(userMsg)) reply = 'Awesome — do mornings or afternoons work better for a 10-min call?';
    else if (/no|not interested/i.test(userMsg)) reply = 'Totally understand. Want me to email the info instead?';
    return { ok: true, reply, model: bot?.outbound?.aiModel || 'gpt-4o-mini', latencyMs: 320 };
  }))) return r.value;
  if ((r = m(/^\/aibots\/([^/]+)\/(pause|activate)$/, (mt) => {
    const bot = STATE.aibots.find((x) => x._id === mt[1]);
    if (bot) bot.status = mt[2] === 'pause' ? 'paused' : 'active';
    return bot;
  }))) return r.value;
  if ((r = m(/^\/aibots\/([^/]+)\/send$/, () => ({ ok: true, sent: true })))) return r.value;
  if ((r = m(/^\/aibots\/([^/]+)$/, (mt, b) => {
    const bot = STATE.aibots.find((x) => x._id === mt[1]);
    if (method === 'GET') return bot || null;
    if (method === 'PUT' && bot) { Object.assign(bot, b, { updatedAt: now() }); return bot; }
    if (method === 'DELETE') { STATE.aibots = STATE.aibots.filter((x) => x._id !== mt[1]); return { ok: true }; }
    return null;
  }))) return r.value;

  // A2P 10DLC
  if (url === '/a2p' && method === 'GET') return STATE.a2p;
  if (url === '/a2p/brand' && method === 'PUT') {
    STATE.a2p.brand = { ...STATE.a2p.brand, ...body };
    STATE.a2p.events.push({ type: 'BRAND_UPDATED', message: 'Brand details updated', at: now() });
    return STATE.a2p;
  }
  if (url === '/a2p/brand/submit' && method === 'POST') {
    STATE.a2p.brandStatus = 'SUBMITTED';
    STATE.a2p.events.push({ type: 'BRAND_SUBMITTED', message: 'Submitted to TCR', at: now() });
    // Simulate fast approval after a beat
    setTimeout(() => {
      STATE.a2p.brandStatus = 'APPROVED';
      STATE.a2p.brandId = STATE.a2p.brandId || ('BN-' + uid().toUpperCase());
      STATE.a2p.events.push({ type: 'BRAND_APPROVED', message: 'Brand verified', at: now() });
    }, 3000);
    return STATE.a2p;
  }
  if (url === '/a2p/campaign' && method === 'PUT') {
    STATE.a2p.campaign = { ...STATE.a2p.campaign, ...body };
    STATE.a2p.events.push({ type: 'CAMPAIGN_UPDATED', message: 'Campaign details updated', at: now() });
    return STATE.a2p;
  }
  if (url === '/a2p/campaign/submit' && method === 'POST') {
    STATE.a2p.campaignStatus = 'SUBMITTED';
    STATE.a2p.events.push({ type: 'CAMPAIGN_SUBMITTED', message: 'Submitted for carrier review', at: now() });
    setTimeout(() => {
      STATE.a2p.campaignStatus = 'APPROVED';
      STATE.a2p.campaignId = STATE.a2p.campaignId || ('CAMP-' + uid().toUpperCase());
      STATE.a2p.events.push({ type: 'CAMPAIGN_APPROVED', message: 'Carrier approval received', at: now() });
    }, 3000);
    return STATE.a2p;
  }
  if (url === '/a2p/numbers/attach' && method === 'POST') {
    const num = body?.number;
    if (num && !STATE.a2p.attachedNumbers.includes(num)) {
      STATE.a2p.attachedNumbers.push(num);
      STATE.a2p.events.push({ type: 'NUMBER_ATTACHED', message: `${num} attached to campaign`, at: now() });
    }
    return STATE.a2p;
  }

  // Marketing presets
  if (url === '/marketing/sms/presets' && method === 'GET') {
    return [
      { _id: uid(), name: 'Intro', body: 'Hi #first_name, this is Easton with CIVIX. Got a sec?' },
      { _id: uid(), name: 'Quote Ready', body: '#first_name your quote is ready. Coverage starts at $#quote/mo.' },
      { _id: uid(), name: 'Reschedule', body: 'Hey #first_name — missed you. When works to chat?' },
    ];
  }
  if (url === '/marketing/email/presets' && method === 'GET') {
    return [
      { _id: uid(), name: 'Welcome', subject: 'Welcome to CIVIX', body: 'Hi #first_name, ...' },
      { _id: uid(), name: 'Policy Issued', subject: 'Your policy is active', body: 'Congratulations #first_name ...' },
    ];
  }
  if (url === '/marketing/keywords' && method === 'GET') {
    return [
      { _id: uid(), keyword: 'QUOTE', autoReply: 'Reply with your ZIP and DOB for an instant quote.' },
      { _id: uid(), keyword: 'STOP', autoReply: 'You have been unsubscribed.' },
    ];
  }

  // Notifications
  if (url === '/notifications' && method === 'GET') return STATE.notifications;
  if ((r = m(/^\/notifications\/([^/]+)\/read$/, (mt) => { const n = STATE.notifications.find((x) => x._id === mt[1]); if (n) n.read = true; return { ok: true }; }))) return r.value;

  // Billing
  if (url === '/billing/subscription' && method === 'GET') {
    return { plan: 'Pro', price: 99, status: 'active', renewsAt: dayjs().add(18, 'day').toISOString() };
  }
  if (url === '/billing/funds' && method === 'GET') {
    return { balance: 29.99, credits: 16497, minutes: 16699, autoRecharge: { enabled: true, threshold: 5, amount: 25 } };
  }
  if (url === '/billing/addons' && method === 'GET') {
    return [
      { id: 'ai-tools', name: 'AI Tools', price: 10, active: true },
      { id: 'power-dialer-voice', name: 'Power Dialer + AI Voice', price: 70, active: true },
      { id: 'call-scripts', name: 'AI Call Scripts', price: 10, active: false },
      { id: 'skip-beep', name: 'Skip-Beep VM Drop', price: 10, active: true },
    ];
  }
  if (url === '/billing/history' && method === 'GET') {
    return Array.from({ length: 6 }, (_, i) => ({ _id: uid(), date: dayjs().subtract(i, 'month').format('YYYY-MM-DD'), description: 'CIVIX Pro Plan', amount: 99, status: 'paid' }));
  }

  // Pipeline
  if (url === '/pipeline' && method === 'GET') {
    return STAGES.map((s) => ({ stage: s, leads: STATE.leads.filter((l) => l.stage === s).slice(0, 8) }));
  }

  // Agents / team
  if (url === '/agents' && method === 'GET') {
    return [
      { _id: uid(), name: 'Easton Padden', email: 'easton@civix.local', role: 'superadmin', online: true },
      { _id: uid(), name: 'Sarah Manager', email: 'manager@civix.local', role: 'manager', online: true },
      { _id: uid(), name: 'Agent One', email: 'agent1@civix.local', role: 'agent', online: false },
      { _id: uid(), name: 'Agent Two', email: 'agent2@civix.local', role: 'agent', online: true },
    ];
  }

  // Onboarding
  if (url === '/onboarding/status' && method === 'GET') {
    return { steps: [
      { id: 'profile', label: 'Complete profile', done: true },
      { id: 'phone', label: 'Get a phone number', done: true },
      { id: 'vendor', label: 'Connect a lead vendor', done: true },
      { id: 'campaign', label: 'Launch first campaign', done: false },
      { id: 'dialer', label: 'Make 10 calls', done: false },
    ]};
  }

  // ----------- DNC / TCPA scrubbing -----------
  if (url === '/dnc/status' && method === 'GET') {
    return { provider: 'blacklistAlliance', configured: false, mode: 'demo', supports: ['scrubPhone', 'scrubBatch'] };
  }
  if (url === '/dnc/scrub' && method === 'POST') {
    const phone = String(body?.phone || '').replace(/\D/g, '');
    const last = parseInt(phone.slice(-1) || '0', 10);
    const status = last <= 5 ? 'clean' : last === 6 ? 'wireless' : last === 7 ? 'dnc' : last === 8 ? 'litigator' : 'unknown';
    return { phone, status, blocked: status === 'dnc' || status === 'litigator', provider: 'blacklistAlliance', checkedAt: now() };
  }
  if (url === '/dnc/scrub-batch' && method === 'POST') {
    const phones = Array.isArray(body?.phones) ? body.phones : [];
    const results = phones.map((p) => {
      const phone = String(p || '').replace(/\D/g, '');
      const last = parseInt(phone.slice(-1) || '0', 10);
      const status = last <= 5 ? 'clean' : last === 6 ? 'wireless' : last === 7 ? 'dnc' : last === 8 ? 'litigator' : 'unknown';
      return { phone, status, blocked: status === 'dnc' || status === 'litigator' };
    });
    const summary = { clean: 0, dnc: 0, litigator: 0, wireless: 0, invalid: 0, unknown: 0 };
    results.forEach((r) => { summary[r.status] = (summary[r.status] || 0) + 1; });
    return { ok: true, total: results.length, results, summary, provider: 'blacklistAlliance' };
  }

  // ----------- Carriers (e-app launcher) -----------
  if (url === '/carriers' && method === 'GET') return CARRIERS;
  if ((r = m(/^\/carriers\/([^/]+)$/, (mt) => CARRIERS.find((c) => c.id === mt[1]) || null))) return r.value;
  if (url === '/carriers/meta/products' && method === 'GET') {
    const set = new Set();
    CARRIERS.forEach((c) => c.products.forEach((p) => set.add(p)));
    return Array.from(set).sort();
  }
  if ((r = m(/^\/carriers\/([^/]+)\/launch$/, (mt) => {
    const carrier = CARRIERS.find((c) => c.id === mt[1]);
    if (!carrier) return null;
    const lead = body?.lead || {};
    const params = new URLSearchParams();
    if (lead.firstName) params.set('first', lead.firstName);
    if (lead.lastName) params.set('last', lead.lastName);
    if (lead.email) params.set('email', lead.email);
    if (lead.phone) params.set('phone', lead.phone);
    if (lead.state) params.set('state', lead.state);
    const sep = carrier.agentPortalUrl.includes('?') ? '&' : '?';
    const url = `${carrier.agentPortalUrl}${params.toString() ? sep + params.toString() : ''}`;
    return { ok: true, carrier: carrier.id, name: carrier.name, launchUrl: url, openInNewTab: true };
  }))) return r.value;

  // ----------- Status / health -----------
  if (url === '/status' && method === 'GET') {
    return {
      ok: true,
      status: 'ok',
      time: now(),
      version: '1.0.0',
      checks: {
        mongo: { ok: true, status: 'connected', latencyMs: 12 },
        dnc: { ok: true, status: 'configured', provider: 'blacklistAlliance', latencyMs: 41 },
        twilio: { ok: true, status: 'connected', latencyMs: 67 },
        sentry: { ok: true, status: 'scaffolded', dsn: 'placeholder' },
      },
    };
  }
  if (url === '/healthz' && method === 'GET') return { ok: true, time: now() };
  if (url === '/readyz' && method === 'GET') return { ok: true, ready: true, time: now() };

  // Generic catch-alls
  if (method === 'GET') return [];
  if (method === 'POST' || method === 'PUT') return { ok: true, ...body };
  if (method === 'DELETE') return { ok: true };
  return null;
}

export function isDemoMode() {
  return import.meta.env.VITE_DEMO_MODE === 'true';
}

export async function demoRequest(method, url, body, params) {
  // Strip leading /api if present
  const u = url.replace(/^\/api/, '');
  await new Promise((res) => setTimeout(res, 80 + Math.random() * 200));
  const result = dispatch(method, u, body, params);
  if (result === null) {
    const err = new Error(`Demo: no handler for ${method} ${u}`);
    err.response = { status: 404, data: { error: 'Not found in demo' } };
    throw err;
  }
  return result;
}
